/**
 * Column definitions for the All Sources table.
 *
 * Mirrors the journal-articles columns but is oriented around source-type
 * agnostic fields: the article-specific topic/extraction-status columns are
 * dropped, and the "Source Type" column is promoted so it is visible by
 * default in every view.
 */

import { ColumnGroup } from "@/components/shared/ManageColumnsDialog"
import { ViewMode } from "@/components/shared/TableViewSelector"

/**
 * Column groups for the compact and expanded views.
 * These views share the same set of columns.
 */
const COMPACT_COLUMN_GROUPS: ColumnGroup<string>[] = [
  { keys: ["sourceId"], label: "Source ID" },
  { keys: ["title"], label: "Title" },
  { keys: ["sourceType"], label: "Source Type" },
  { keys: ["publicationYear"], label: "Year" },
  { keys: ["journaltitle"], label: "Journal" },
  { keys: ["doi"], label: "DOI" },
  { keys: ["mapSurveyId"], label: "MAP Survey ID" },
  { keys: ["surveyType"], label: "Survey Type" },
]

/**
 * Column groups for the full view.
 * Shows every available source-type-agnostic column individually.
 */
const FULL_COLUMN_GROUPS: ColumnGroup<string>[] = [
  { keys: ["sourceId"], label: "Source ID" },
  { keys: ["title"], label: "Title" },
  { keys: ["sourceType"], label: "Source Type" },
  { keys: ["authors"], label: "Authors" },
  { keys: ["journaltitle"], label: "Journal" },
  { keys: ["publicationYear"], label: "Year" },
  { keys: ["volume"], label: "Volume" },
  { keys: ["issue"], label: "Issue" },
  { keys: ["pages"], label: "Pages" },
  { keys: ["doi"], label: "DOI" },
  { keys: ["doiUrl"], label: "DOI URL" },
  { keys: ["pubmedId"], label: "PubMed ID" },
  { keys: ["pubmedUrl"], label: "PubMed URL" },
  { keys: ["ihmeNid"], label: "IHME NID" },
  { keys: ["mapSurveyId"], label: "MAP Survey ID" },
  { keys: ["surveyType"], label: "Survey Type" },
  { keys: ["abstract"], label: "Abstract" },
  { keys: ["citation"], label: "Citation" },
  { keys: ["suggestedCitation"], label: "Suggested Citation" },
  { keys: ["url"], label: "URL" },
  { keys: ["city"], label: "City" },
  { keys: ["institution"], label: "Institution" },
  { keys: ["institutions"], label: "Institutions" },
  { keys: ["contacts"], label: "Contacts" },
  { keys: ["reportNumber"], label: "Report Number" },
  { keys: ["researchNotes"], label: "Research Notes" },
  { keys: ["usagePermission"], label: "Usage Permission" },
  { keys: ["communicationDate"], label: "Communication Date" },
  { keys: ["dateUrlVisited"], label: "Date URL Visited" },
  { keys: ["createdBy"], label: "Created By" },
  { keys: ["createdDate"], label: "Created Date" },
  { keys: ["lastModifiedBy"], label: "Last Modified By" },
  { keys: ["lastModifiedDate"], label: "Last Modified Date" },
]

/**
 * Returns the column groups appropriate for the given view mode.
 *
 * The All Sources page only offers the compact and full views (no
 * extraction-status view, which is article-specific).
 *
 * @param viewMode - The currently active view mode.
 * @returns Array of column groups for the Manage Columns dialog.
 */
export function getColumnGroupsForViewMode(viewMode: ViewMode): ColumnGroup<string>[] {
  if (viewMode === "full") {
    return FULL_COLUMN_GROUPS
  }
  return COMPACT_COLUMN_GROUPS
}

/** All column keys across all views (used for default visibility state). */
export const ALL_SOURCE_KEYS: Set<string> = new Set(
  FULL_COLUMN_GROUPS.flatMap((g) => g.keys)
)
